import React, { useEffect, useState } from 'react';
import { CONTAINERS } from '../../../constants/columns';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { carsOptions } from '../../../constants/filterOptions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import getContainers from '../../../api/cars/getContainers';

// components
import Header from '../../../components/Header/Header';
import Table from '../../../components/Table/Table';
import TableTabs from '../../../components/TableTabs/TableTabs';
import Empty from '../../../components/Empty/Empty';

// icons
import icon from '../../../brand/generated/sidebar/cars.svg';

const columns = CONTAINERS;

// Поиск и страница живут в адресе и в sessionStorage. Менеджер проходит по
// моделям марки, правя комплектации: провалился в CHEVROLET Tracker, вернулся —
// и должен увидеть тот же список CHEVROLET на той же странице, а не первую
// страницу общего списка. Адрес закрывает кнопку «назад» в браузере,
// sessionStorage — возврат через меню «Марка и модель».
const STORAGE_KEY = 'containers.search';

const readStoredSearch = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const storeSearch = (search) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(search));
  } catch {
    // приватный режим — обойдёмся адресом
  }
};

export const restoreSearch = (searchParams) => {
  const fromUrl = searchParams.has('q') || searchParams.has('page');
  const saved = fromUrl
    ? {
        q: searchParams.get('q') ?? '',
        page: Number(searchParams.get('page')),
        pageSize: Number(searchParams.get('pageSize')),
      }
    : readStoredSearch();
  return {
    q: (saved.q ?? '').trim(),
    page: saved.page > 0 ? saved.page : 1,
    pageSize: saved.pageSize > 0 ? saved.pageSize : 10,
  };
};

const CarContainers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [restored] = useState(() => restoreSearch(searchParams));
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pagination, setPagination] = useState({
    page: restored.page,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: restored.pageSize,
  });
  const [isFetchAll, setFetchAll] = useState(false);

  const table = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: pagination.pageSize,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { page, setPageSize } = table;

  const [value, setValue] = useState(restored.q);
  const [queryParams, setQueryParams] = useState(restored.q ? { markAndModel: restored.q } : {});

  // Быстрый поиск раньше фильтровал только загруженную страницу (10 строк), и
  // «KIA» находил в лучшем случае один-два контейнера. Теперь запрос уходит на
  // сервер: «KIA» — все контейнеры марки, «KIA Sportage» — один. Так менеджер
  // получает список моделей марки и проходит по ним, правя комплектации.
  const searchOnServer = (query) => {
    const markAndModel = (query ?? '').trim();
    setQueryParams(markAndModel ? { markAndModel } : {});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleNavigate = () => {
    navigate('/cars/containers/create');
  };

  const goToCard = (index) => {
    navigate('/cars/containers/' + index);
  };

  const init = (page) => {
    setLoading(true);
    getContainers({ ...page })
      .then((res) => {
        setData(res.data.containers);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const search = {
      q: queryParams.markAndModel ?? '',
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
    storeSearch(search);
    setSearchParams(
      { ...(search.q ? { q: search.q } : {}), page: String(search.page), pageSize: String(search.pageSize) },
      { replace: true }
    );
    if (!isFetchAll) {
      init({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams]);

  return (
    <>
      <Header title="Марка и модель" icon={icon} />
      <TableTabs
        setFilter={searchOnServer}
        options={carsOptions}
        value={value}
        setValue={setValue}
        navigate={handleNavigate}
        button={'Добавить контейнер'}
        inputPlaceholder={'Быстрый поиск по маркам и моделям'}
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          table={table}
          handleNavigate={goToCard}
          all={'марок'}
          p={pagination}
          loading={loading}
          pagination
          setPagination={setPagination}
          setFetchAll={setFetchAll}
          isFetchAll={isFetchAll}
          setData={setData}
          setLoading={setLoading}
          queryParams={queryParams}
          api={'/api/shop/getContainers'}
          response={'containers'}
        />
      )}
    </>
  );
};

export default CarContainers;
